/** What kind of value an Edit-table cell takes, and the typing hints that
 *  follow from it. Hints only: nothing here blocks a keystroke, the live
 *  validation (clientRowErrors) stays the judge of what is valid. */
import type { InputHTMLAttributes } from 'react';
import type { ExpectedField } from '@discovr/types';
import { MAX_RAW_LENGTH } from './amountParse';
import { MAX_NAME_LENGTH, USAGE_RIGHTS_DAYS_KEY, USERNAME_KEY } from './importWizardHelpers';

export type ReviewInputKind = 'identity' | 'name' | 'whole' | 'decimal' | 'price' | 'days' | 'text';

export type ReviewInputAttributes = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'inputMode' | 'maxLength' | 'autoCapitalize' | 'autoCorrect' | 'spellCheck'
>;

/** Room for any pasted profile link, tracking parameters included. */
const MAX_IDENTITY_LENGTH = 2048;
/** `maxLength` counts UTF-16 units, the name rule counts code points, and a
 *  code point takes up to two units (an emoji). The cap therefore leaves room
 *  for the longest valid name; nameError stays the judge of the real limit. */
const MAX_UTF16_UNITS_PER_CODE_POINT = 2;
const MAX_NAME_INPUT_UNITS = MAX_NAME_LENGTH * MAX_UTF16_UNITS_PER_CODE_POINT;

const KIND_BY_KEY: ReadonlyMap<string, ReviewInputKind> = new Map([
  [USERNAME_KEY, 'identity'],
  ['first_name', 'name'],
  ['last_name', 'name'],
  ['followers', 'whole'],
  ['avg_views', 'decimal'],
  ['engagement_rate', 'decimal'],
  [USAGE_RIGHTS_DAYS_KEY, 'days'],
]);

const DELIVERABLE_KIND: ReviewInputKind = 'price';
const FALLBACK_KIND: ReviewInputKind = 'text';

const ATTRIBUTES_BY_KIND: Record<ReviewInputKind, ReviewInputAttributes> = {
  // A handle or link is never a dictionary word: no capitalising, no fixing.
  identity: { inputMode: 'text', maxLength: MAX_IDENTITY_LENGTH, autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false },
  name: { inputMode: 'text', maxLength: MAX_NAME_INPUT_UNITS },
  // Not a digits-only keypad: followers also accepts "12k" and "12.5k".
  whole: { inputMode: 'text', maxLength: MAX_RAW_LENGTH },
  decimal: { inputMode: 'decimal', maxLength: MAX_RAW_LENGTH },
  // Free text on purpose: "12k", "₹5,000" and "remove" are all legal prices.
  price: { inputMode: 'text', maxLength: MAX_RAW_LENGTH },
  days: { inputMode: 'numeric', maxLength: MAX_RAW_LENGTH },
  text: { inputMode: 'text' },
};

export function reviewInputKind(field: ExpectedField): ReviewInputKind {
  const known = KIND_BY_KEY.get(field.key);
  if (known !== undefined) return known;
  return field.isDeliverable ? DELIVERABLE_KIND : FALLBACK_KIND;
}

export function reviewInputAttributes(kind: ReviewInputKind): ReviewInputAttributes {
  return ATTRIBUTES_BY_KIND[kind];
}
